import { useEffect } from 'react';

import { useSocialLoginFlag } from './useSocialLoginFlag';

/**
 * Registration flow types based on feature flag combinations
 */
type RegistrationFlow =
  | { type: 'loading' }
  | { type: 'full-featured' } // SocialLogin enabled: Cognito + SocialLogin
  | { type: 'new-registration' }; // No social login

/**
 * Centralized hook to determine which registration flow to use based on feature flags.
 *
 * This hook consolidates the feature flag checking logic that was previously duplicated
 * across SocialLandingRoute and EmailRegistrationRoute.
 *
 * ## Feature Flag Combinations
 *
 * - **loading**: Social login flag is still loading
 * - **full-featured**: SocialLogin enabled
 *   - Shows social login landing page at /register
 *   - Shows email registration form at /register/email
 * - **new-registration**: Social login not enabled
 *   - Shows new registration form without social login options
 *
 * ## Post-Registration Navigation
 *
 * The registration form itself determines where to navigate after successful registration
 * based on the `enable-new-trader-onboarding` flag:
 * - If onboarding enabled: Navigate to `/onboarding` for tutorials
 * - If onboarding disabled: Navigate directly to `/verify-email`
 *
 * This separation ensures the registration UI is decoupled from post-registration flow.
 *
 * ## Side Effects
 *
 * This hook automatically cleans up the `trmPath` query parameter from the URL.
 * This parameter is used for post-authentication redirects and should not persist
 * in the registration flow.
 *
 * @example
 * ```tsx
 * const MyRouteComponent = () => {
 *   const flow = useRegistrationFlow();
 *
 *   if (flow.type === 'loading') return <LoadingIndicator />;
 *   if (flow.type === 'full-featured') return <NewFlowWithSocial />;
 *   return <NewFlowWithoutSocial />;
 * };
 * ```
 *
 * @returns {RegistrationFlow} The current registration flow type
 */
export const useRegistrationFlow = (): RegistrationFlow => {
  const { isSocialLoginEnabled } = useSocialLoginFlag();

  // Clean up trmPath parameter from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const trmPath = params.get('trmPath');

    if (trmPath) {
      params.delete('trmPath');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, []);

  // Social login enabled = full-featured flow
  if (isSocialLoginEnabled) {
    return { type: 'full-featured' };
  }

  // Otherwise, new registration without social login
  return { type: 'new-registration' };
};
